import { useState, useEffect, useCallback } from 'react';
import type { DataProvider } from 'react-admin';

export interface MockDisciplinaryCase {
  id: string;
  caseNumber: string;
  employeeId: string;
  employeeName: string;
  department: string;
  incidentDate: string;
  description: string;
  currentStage: string;
  hearingDate: string | null;
  outcome: string | null;
  warningType: string | null;
}

export interface MockTrainingRecord {
  id: string;
  employeeId: string;
  employeeName: string;
  courseName: string;
  provider: string;
  startDate: string;
  endDate: string;
  status: string;
  interventionType: string;
  cost: number;
  durationHours: number;
  ofocCode: string;
}

export interface MockOhsIncident {
  id: string;
  employeeId: string;
  referenceNumber: string;
  employeeName: string;
  incidentDate: string;
  type: string;
  severity: string;
  dolReported: boolean;
  status: string;
}

export interface MockPip {
  id: string;
  caseNumber: string;
  employeeId: string;
  employeeName: string;
  department: string;
  openedDate: string;
  targetDate: string;
  reviewDate: string;
  status: 'Draft' | 'Active' | 'Review' | 'Completed' | 'Terminated';
  performanceIssues: string;
  improvementPlan: string;
  outcome: string | null;
}

export interface MockOhsCertification {
  id: string;
  employeeId: string;
  employeeName: string;
  certificationType: string;
  status: string;
  certifiedAt: string;
  expiryDate: string;
}

export interface PriorContextOptions {
  dataProvider?: DataProvider;
  employeeId: string;
}

export interface PriorContextReturn {
  priorWarnings: MockDisciplinaryCase[];
  priorTraining: MockTrainingRecord[];
  priorIncidents: MockOhsIncident[];
  priorPIPs: MockPip[];
  priorCertifications: MockOhsCertification[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export type EscalationLevel = 'none' | 'verbal' | 'written' | 'finalWritten';

export function calculateEscalationLevel(
  priorWarnings: MockDisciplinaryCase[]
): EscalationLevel {
  const warningsWithType = priorWarnings.filter(w => w.warningType !== null);

  if (warningsWithType.length === 0) {
    return 'none';
  }

  if (warningsWithType.length >= 2) {
    return 'finalWritten';
  }

  const onlyWarning = warningsWithType[0];
  if (onlyWarning.warningType === 'Verbal') {
    return 'written';
  }

  if (onlyWarning.warningType === 'Written') {
    return 'finalWritten';
  }

  return 'finalWritten';
}

export interface TrainingHistorySummary {
  totalRecords: number;
  totalHours: number;
  totalCost: number;
  lastCourse: string | null;
}

export function summarizeTrainingHistory(
  records: MockTrainingRecord[]
): TrainingHistorySummary {
  if (records.length === 0) {
    return {
      totalRecords: 0,
      totalHours: 0,
      totalCost: 0,
      lastCourse: null,
    };
  }

  const sortedRecords = [...records].sort(
    (a, b) => new Date(b.endDate).getTime() - new Date(a.endDate).getTime()
  );

  return {
    totalRecords: records.length,
    totalHours: records.reduce((sum, r) => sum + r.durationHours, 0),
    totalCost: records.reduce((sum, r) => sum + r.cost, 0),
    lastCourse: sortedRecords[0]?.courseName ?? null,
  };
}

export function usePriorContext({
  dataProvider,
  employeeId,
}: PriorContextOptions): PriorContextReturn {
  const [priorWarnings, setPriorWarnings] = useState<MockDisciplinaryCase[]>([]);
  const [priorTraining, setPriorTraining] = useState<MockTrainingRecord[]>([]);
  const [priorIncidents, setPriorIncidents] = useState<MockOhsIncident[]>([]);
  const [priorPIPs, setPriorPIPs] = useState<MockPip[]>([]);
  const [priorCertifications, setPriorCertifications] = useState<MockOhsCertification[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPriorRecords = useCallback(async () => {
    if (!dataProvider || !employeeId) {
      setPriorWarnings([]);
      setPriorTraining([]);
      setPriorIncidents([]);
      setPriorPIPs([]);
      setPriorCertifications([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const fetchWarnings = dataProvider.getManyReference('disciplinary-cases', {
        target: 'employeeId',
        id: employeeId,
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'incidentDate', order: 'DESC' },
        filter: {},
      });

      const fetchTraining = dataProvider.getManyReference('training-records', {
        target: 'employeeId',
        id: employeeId,
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'startDate', order: 'DESC' },
        filter: {},
      });

      const fetchIncidents = dataProvider.getManyReference('workplace-injuries', {
        target: 'employeeId',
        id: employeeId,
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'incidentDate', order: 'DESC' },
        filter: {},
      });

      const fetchPIPs = dataProvider.getManyReference('pips', {
        target: 'employeeId',
        id: employeeId,
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'openedDate', order: 'DESC' },
        filter: {},
      });

      const fetchCertifications = dataProvider.getManyReference('ohs-certifications', {
        target: 'employeeId',
        id: employeeId,
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'certifiedAt', order: 'DESC' },
        filter: {},
      });

      const [warningsResult, trainingResult, incidentsResult, pipsResult, certificationsResult] =
        await Promise.all([
          fetchWarnings,
          fetchTraining,
          fetchIncidents,
          fetchPIPs,
          fetchCertifications,
        ]);

      setPriorWarnings((warningsResult.data as MockDisciplinaryCase[]) ?? []);
      setPriorTraining((trainingResult.data as MockTrainingRecord[]) ?? []);
      setPriorIncidents((incidentsResult.data as MockOhsIncident[]) ?? []);
      setPriorPIPs((pipsResult.data as MockPip[]) ?? []);
      setPriorCertifications((certificationsResult.data as MockOhsCertification[]) ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch prior records');
      setPriorWarnings([]);
      setPriorTraining([]);
      setPriorIncidents([]);
      setPriorPIPs([]);
      setPriorCertifications([]);
    } finally {
      setIsLoading(false);
    }
  }, [dataProvider, employeeId]);

  useEffect(() => {
    fetchPriorRecords();
  }, [fetchPriorRecords]);

  return {
    priorWarnings,
    priorTraining,
    priorIncidents,
    priorPIPs,
    priorCertifications,
    isLoading,
    error,
    refetch: fetchPriorRecords,
  };
}
